import React, { useState } from "react";
import { Search, Star, ShieldCheck, ArrowRight, Play } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { EXPERTS, SITE_SETTINGS } from "../../mock/mock";
import { Button } from "../ui/button";

const Hero = ({ onSearch, content }) => {
  const { t, lang } = useLang();
  const [q, setQ] = useState("");
  const customTitle = content?.[lang]?.heroTitle;

  const avatars = EXPERTS.slice(0, 5);

  const submit = (e) => {
    e.preventDefault();
    onSearch?.(q);
    document.querySelector("#experts")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative pt-36 pb-20 sm:pt-44 sm:pb-28 overflow-hidden">
      <div className="absolute inset-0 hero-grid-bg pointer-events-none" />
      {/* soft accent blobs */}
      <div className="absolute -top-24 -end-24 w-96 h-96 rounded-full bg-indigo-100/50 blur-3xl pointer-events-none animate-float" />
      <div className="absolute top-40 -start-24 w-80 h-80 rounded-full bg-rose-100/40 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-neutral-200 shadow-sm text-sm font-medium text-neutral-700 mb-7 animate-fade-in">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          {t("hero.badge")}
        </div>

        <h1 className="font-display font-extrabold tracking-tight text-neutral-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-balance">
          {customTitle ? customTitle : (
            <>
              {t("hero.title")}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent">
                  {t("hero.titleHighlight")}
                </span>
                <svg className="absolute -bottom-2 start-0 w-full" height="12" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none">
                  <path d="M2 9C50 3 150 3 198 9" stroke="#818cf8" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </span>
            </>
          )}
        </h1>

        <p className="mt-7 text-lg sm:text-xl text-neutral-500 max-w-2xl mx-auto leading-relaxed text-balance">
          {t("hero.subtitle")}
        </p>

        <form onSubmit={submit} className="mt-9 max-w-xl mx-auto">
          <div className="flex items-center gap-2 bg-white rounded-full border border-neutral-200 shadow-lg shadow-neutral-200/50 p-1.5 focus-within:ring-2 focus-within:ring-neutral-900/10 transition">
            <div className="ps-4 text-neutral-400"><Search className="w-5 h-5" /></div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("hero.searchPlaceholder")}
              className="flex-1 bg-transparent outline-none text-neutral-800 placeholder:text-neutral-400 py-2.5"
            />
            <Button type="submit" className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-6 h-11">
              {t("hero.cta")}
            </Button>
          </div>
        </form>

        {/* social proof */}
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <div className="flex -space-x-3 rtl:space-x-reverse">
            {avatars.map((a) => (
              <img key={a.id} src={a.photo} alt={a.name} className="w-9 h-9 rounded-full border-2 border-white object-cover" />
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-neutral-600">
            <div className="flex text-amber-400">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400" />)}
            </div>
            <span className="font-semibold text-neutral-800">{SITE_SETTINGS.stats.rating}</span>
          </div>
        </div>

        {/* stats */}
        <div className="mt-14 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
          {[
            { v: SITE_SETTINGS.stats.experts, k: "stat1" },
            { v: SITE_SETTINGS.stats.sessions, k: "stat2" },
            { v: SITE_SETTINGS.stats.rating, k: "stat3" },
          ].map((s) => (
            <div key={s.k} className="text-center">
              <div className="font-display font-extrabold text-2xl sm:text-4xl text-neutral-900">{s.v}</div>
              <div className="text-xs sm:text-sm text-neutral-500 mt-1">{t(`hero.${s.k}`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
